import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  getDate,
  getDay,
  getDaysInMonth,
  getHours,
  getMinutes,
  getMonth,
  getWeekOfMonth,
  getYear,
  isBefore,
  setDate,
  setYear,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useDateFns } from './useDateFns';
import { useStringFunctions } from './useStringFunctions';
import { useUtilities } from './useUtilities';
import { DaysOfWeek } from '@apparatix/schemas/Scheduling';
import type {
  DayOfMonth,
  DayOfWeek,
  Month,
  Schedule,
  WeekOfMonth,
} from '@apparatix/schemas/Scheduling';
import { createObjectFromSchema } from '@apparatix/schemas/Helpers';
import { scheduleSchema, validateSchedule, type RecurrenceOption } from '@apparatix/schemas/Scheduling';

const fullDayMap: Record<DayOfWeek, string> = {
  Sun: 'Sunday',
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
};

const dayOfWeekNumberToNameMap: Record<number, DayOfWeek> = {
  0: 'Sun',
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
};

const nameOfWeekdayToNumberMap: Record<DayOfWeek, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const fullMonthMap: Record<Month, string> = {
  Jan: 'January',
  Feb: 'February',
  Mar: 'March',
  Apr: 'April',
  May: 'May',
  Jun: 'June',
  Jul: 'July',
  Aug: 'August',
  Sep: 'September',
  Oct: 'October',
  Nov: 'November',
  Dec: 'December',
};

// Javascript dates use zero indexed month numbers. Eg new Date(2000, 5) => 'June 2000'
const monthNumberToNameMap: Record<number, Month> = {
  0: 'Jan',
  1: 'Feb',
  2: 'Mar',
  3: 'Apr',
  4: 'May',
  5: 'Jun',
  6: 'Jul',
  7: 'Aug',
  8: 'Sep',
  9: 'Oct',
  10: 'Nov',
  11: 'Dec',
};

const monthNameToNumberMap: Record<Month, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

const numberToWeekOfMonthMap: Record<number, WeekOfMonth> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'last',
};

const weekOfMonthToNumberMap: Record<WeekOfMonth, number> = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  last: 5,
};

const SCHEDULE_SAVED_KEY = '__hasBeenSaved';

export const useScheduling = () => {
  const { arrayHasValues, deepClone, developerWarning } = useUtilities();
  const {
    formatDate,
    formatTimeFromDate,
    isDayOfWeek,
    parse,
  } = useDateFns();
  const { formatNumberAsOrdinal } = useStringFunctions();

  const sortDays = (a: DayOfWeek, b: DayOfWeek) => DaysOfWeek.options.indexOf(a) - DaysOfWeek.options.indexOf(b);

  const formatDaily = (schedule: Schedule) => {
    if (!schedule.occursEvery) {
      return '';
    }

    if (schedule.occursEvery === 1) {
      return 'Occurs every day';
    }

    if (schedule.occursEvery === 2) {
      return 'Occurs every other day';
    }

    return `Occurs every ${schedule.occursEvery} days`;
  };

  const formatWeekly = (schedule: Schedule) => {
    if (!arrayHasValues(schedule.selectedDays)) return '';

    const sortedDays = schedule.selectedDays.toSorted(sortDays);

    if (sortedDays.length === DaysOfWeek.options.length) {
      return 'Occurs every day';
    }

    if (arrayHasValues(sortedDays, { exactly: 1 })) {
      return `Occurs every ${fullDayMap[sortedDays[0]]}`;
    }

    if (arrayHasValues(sortedDays, { exactly: 2 })) {
      return `Occurs every ${fullDayMap[sortedDays[0]]} and ${fullDayMap[sortedDays[1]]}`;
    }

    if (
      sortedDays.includes('Mon') &&
      sortedDays.includes('Tue') &&
      sortedDays.includes('Wed') &&
      sortedDays.includes('Thu') &&
      sortedDays.includes('Fri') &&
      !sortedDays.includes('Sun') &&
      !sortedDays.includes('Sat')
    ) {
      return 'Occurs every weekday';
    }

    return `Occurs every ${sortedDays.join(', ')}`;
  };

  const formatMonthly = (schedule: Schedule) => {
    if (schedule.occursOnDay && schedule.selectedDay) {
      return `Occurs on the ${schedule.occursOnDay} ${fullDayMap[schedule.selectedDay]} of each month`;
    }

    const startDate = parse(schedule.startDateTime);
    const day = getDate(startDate);

    return `Occurs on the ${formatNumberAsOrdinal(day)} of each month`;
  };

  const formatYearly = (schedule: Schedule) => {
    if (schedule.occursOnDay && schedule.selectedDay) {
      return `Occurs each year on the ${schedule.occursOnDay} ${fullDayMap[schedule.selectedDay]} of ${fullMonthMap[schedule.selectedMonth]}`;
    }

    const startDate = parse(schedule.startDateTime);
    const month = getMonthNameOfDate(startDate, true);
    const day = getDate(startDate);

    return `Occurs each year on the ${formatNumberAsOrdinal(day)} of ${month}`;
  };

  const formatRecurrence = (schedule: Schedule) => {
    const noSchedule = 'This is a one time event';
    if (!schedule) {
      return noSchedule;
    }

    const startDate = parse(schedule.startDateTime);
    const startTime = formatTimeFromDate(startDate);
    const endDate = schedule.endDate ? parse(schedule.endDate) : undefined;
    const endDateDisplay = endDate ? ` until ${formatDate(endDate, 'eee, MMM do yyyy')}` : '';

    const suffix = `at ${startTime}${endDateDisplay}`;

    const getDisplay = (prefix: string) => `${prefix} ${suffix}`;

    switch (schedule.interval) {
      case 'daily':
        return getDisplay(formatDaily(schedule));
      case 'weekly':
        return getDisplay(formatWeekly(schedule));
      case 'monthly':
        return getDisplay(formatMonthly(schedule));
      case 'yearly':
        return getDisplay(formatYearly(schedule));
      default:
        return noSchedule;
    }
  };

  const getNthOccurrenceOfDayInMonth = (occurrence: WeekOfMonth, day: DayOfWeek, forDate: Date) => {
    const daysInMonth = getDaysInMonth(forDate);
    let beginningOfMonth = startOfMonth(forDate);

    const includedDates: Date[] = [];

    for (let i=1; i<=daysInMonth; i++) {
      if (isDayOfWeek(day, beginningOfMonth)) {
        includedDates.push(beginningOfMonth);
      }

      beginningOfMonth = addDays(beginningOfMonth, 1);
    }

    const occurrenceIndex = weekOfMonthToNumberMap[occurrence] - 1;

    return includedDates[occurrenceIndex] ? includedDates[occurrenceIndex] : includedDates[includedDates.length - 1];
  };

  const hasSchedulingSet = (schedule: Schedule) =>
    schedule.id ||
    schedule.occursEvery ||
    schedule.occursOnDay ||
    schedule.occursOnDayOfMonth ||
    schedule.selectedMonth ||
    arrayHasValues(schedule.selectedDays);

  // This is used to internally track whether a schedule has been saved or not
  // Primarily to prevent the delete button from being enabled when there's not really anything to delete
  const markScheduleSaved = (schedule: object) => {
    if (('id' in schedule) && Boolean(schedule.id)) {
      return;
    }
    Object.defineProperty(schedule, SCHEDULE_SAVED_KEY, { value: true, writable: false, enumerable: true });
  };

  const hasBeenSaved = (schedule: Schedule) => schedule && (schedule.id || ((SCHEDULE_SAVED_KEY in schedule) && schedule[SCHEDULE_SAVED_KEY] === true));

  const getDefaultScheduleValues = (startDate: Date): Partial<Schedule> => ({
    interval: 'weekly',
    selectedDay: getDayOfWeekNameForDate(startDate) as DayOfWeek,
    occursEvery: 3,
    occursOnDay: setOccursOnDay(startDate),
    occursOnDayOfMonth: String(getDate(startDate)) as DayOfMonth,
    selectedDays: [getDayOfWeekNameForDate(startDate) as DayOfWeek],
    selectedMonth: getMonthNameOfDate(startDate) as Month,
  });

  const provideScheduleDefaults = (schedule: Schedule, userDefaults?: Partial<Schedule>): Schedule => {
    const startDate: Date = schedule?.startDateTime
      ? parse(schedule.startDateTime)
      : userDefaults?.startDateTime
        ? parse(userDefaults.startDateTime)
        : new Date();

    const defaultValues = getDefaultScheduleValues(startDate);

    const mergedDefaults = userDefaults ? { ...defaultValues, ...userDefaults } : { ...defaultValues };

    // We need to deeply clone the schedule to ensure that any arrays (eg selectedDays) are pointing to new objects instead of references
    // to the schedule passed as a prop to the recurrence editor
    const generated = schedule
      ? createObjectFromSchema(scheduleSchema, { ...mergedDefaults, ...deepClone(schedule) })
      : createObjectFromSchema(scheduleSchema, mergedDefaults);

    if (hasBeenSaved(schedule)) {
      markScheduleSaved(generated);
    }

    if (!generated.occurrences && !generated.endDate) {
      generated.occurrences = 10;
    }

    return generated;
  };

  const isScheduleValid = (schedule: Schedule, monthlyOption: RecurrenceOption, yearlyOption: RecurrenceOption) => {
    try {
      validateSchedule(schedule, monthlyOption, yearlyOption);
      return true;
    } catch (e) {
      developerWarning(true, e, 'throw');
      return false;
    }
  };

  const getFullDayName = (day: DayOfWeek) => fullDayMap[day];

  const getFullMonthName = (month: Month) => fullMonthMap[month];

  const getMonthNameOfDate = (forDate: Date, longName: boolean = false) => {
    const month = getMonth(forDate);

    return longName
      ? fullMonthMap[monthNumberToNameMap[month]]
      : monthNumberToNameMap[month];
  };

  const getDayOfMonthSelectOptions = (forDate: Date) => {
    const numberOfDays = getDaysInMonth(forDate);

    return [...Array(numberOfDays).keys()].map(key => String(key + 1));
  };

  const getDayOfWeekNameForDate = (forDate: Date, longName: boolean = false) => {
    const day = getDay(forDate);

    return longName
      ? fullDayMap[dayOfWeekNumberToNameMap[day]]
      : dayOfWeekNumberToNameMap[day];
  };

  const setOccursOnDay = (forDate: Date) => {
    const weekNumber = getWeekOfMonth(forDate);
    return numberToWeekOfMonthMap[weekNumber];
  };

  const adjustScheduleStartDate = (
    schedule: Schedule,
    previousStart: Date,
    originalYear: number,
    originalHours: number,
    originalMinutes: number,
    monthlyOption?: RecurrenceOption,
    yearlyOption?: RecurrenceOption,
  ) => {
    const { interval } = schedule;
    if (interval === 'daily') {
      return;
    }

    const restoreTime = (date: Date) => addHours(addMinutes(date, originalMinutes), originalHours);

    const now = new Date();

    /**
     * When referring to scheduling options, 'variable' and 'fixed' are used to differentiate how the schedule date is determined
     * 'fixed' means a specific static date, eg the 15th, or the 29th. The schedule will always be on that specific date
     * 'variable' is used for dynamically calculated dates, eg "the second Saturday of each month", or "The last Monday of June"
     * where the exact date will vary from month to month and year to year
     */

    if (interval === 'weekly') {
      if (!arrayHasValues(schedule.selectedDays)) {
        return;
      }

      const originalDayOfWeek = getDay(previousStart);

      const scheduledDays = schedule.selectedDays?.map(day => nameOfWeekdayToNumberMap[day])?.toSorted();
      if (scheduledDays.includes(originalDayOfWeek)) {
        return;
      }

      const [closestUpcomingDay] = scheduledDays;

      const isClosestNextWeek = closestUpcomingDay < originalDayOfWeek;
      const daysToAdd = isClosestNextWeek
        ? (7 - originalDayOfWeek) + closestUpcomingDay
        : closestUpcomingDay - originalDayOfWeek;

      const newStartDate = addDays(previousStart, daysToAdd);
      schedule.startDateTime = restoreTime(newStartDate).toISOString();
      return;
    }

    if (interval === 'monthly') {
      if (monthlyOption === 'variable') {
        let newDate = getNthOccurrenceOfDayInMonth(schedule.occursOnDay, schedule.selectedDay, previousStart);
        const newYear = getYear(newDate);

        if (newYear !== originalYear) {
          newDate = setYear(newDate, originalYear);
        }

        if (isBefore(newDate, now)) {
          newDate = getNthOccurrenceOfDayInMonth(schedule.occursOnDay, schedule.selectedDay, addMonths(newDate, 1));
        }

        if (schedule.occursOnDayOfMonth) {
          const newDayOfMonth = getDate(newDate);
          schedule.occursOnDayOfMonth = String(newDayOfMonth) as DayOfMonth;
        }
        schedule.startDateTime = restoreTime(newDate).toISOString();
        return;
      }

      let newDate = setDate(previousStart, Number(schedule.occursOnDayOfMonth));

      if (isBefore(newDate, now)) {
        newDate = addMonths(newDate, 1);
      }

      schedule.startDateTime = restoreTime(newDate).toISOString();
    }

    if (interval === 'yearly') {
      const originalDayOfMonth = getDate(previousStart);
      const scheduledDayOfMonth = schedule.occursOnDayOfMonth ? Number(schedule.occursOnDayOfMonth) : undefined;
      const scheduledMonth = monthNameToNumberMap[schedule.selectedMonth];
      const targetDayOfMonth = scheduledDayOfMonth ? scheduledDayOfMonth : originalDayOfMonth;

      const adjustedYear = isBefore(new Date(originalYear, scheduledMonth, scheduledDayOfMonth), now)
        ? originalYear + 1
        : originalYear;

      const targetDate = new Date(adjustedYear, scheduledMonth);
      const daysInTargetMonth = getDaysInMonth(targetDate);

      const adjustedDayOfMonth = daysInTargetMonth < targetDayOfMonth
        ? daysInTargetMonth
        : targetDayOfMonth;

      if (schedule.occursOnDayOfMonth) {
        schedule.occursOnDayOfMonth = String(adjustedDayOfMonth) as DayOfMonth;
      }

      const adjustedDate = new Date(adjustedYear, scheduledMonth, adjustedDayOfMonth, originalHours, originalMinutes);

      if (yearlyOption === 'fixed') {
        schedule.startDateTime = adjustedDate.toISOString();
        return;
      }

      if (yearlyOption === 'variable') {
        const newDate = getNthOccurrenceOfDayInMonth(schedule.occursOnDay, schedule.selectedDay, adjustedDate);
        if (schedule.occursOnDayOfMonth) {
          const newDayOfMonth = getDate(newDate);
          schedule.occursOnDayOfMonth = String(newDayOfMonth) as DayOfMonth;
        }
        schedule.startDateTime = newDate.toISOString();
        return;
      }
    }
  };

  const minutesModelValueToDate = (minutes: number, baseDate: string | Date) => {
    if (minutes === undefined || !baseDate) return undefined;

    const date = baseDate instanceof Date ? baseDate : parse(baseDate);
    if (!date) {
      return undefined;
    }

    const startOfDate = startOfDay(date);
    return addMinutes(startOfDate, minutes);
  };

  const dateToModelTimeMinutes = (baseDate: string | Date) => {
    if (!baseDate) return undefined;

    const date = baseDate instanceof Date ? baseDate : parse(baseDate);
    if (!date) {
      return undefined;
    }

    const hours = getHours(date);
    const minutes = getMinutes(date);

    return (60 * hours) + minutes;
  };

  return {
    adjustScheduleStartDate,
    dateToModelTimeMinutes,
    formatRecurrence,
    getDayOfMonthSelectOptions,
    getFullDayName,
    getFullMonthName,
    hasBeenSaved,
    hasSchedulingSet,
    isScheduleValid,
    markScheduleSaved,
    minutesModelValueToDate,
    provideScheduleDefaults,
  };
};
